"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { toggleVariants } from "@/components/ui/toggle";
const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants> & {
  spacing?: number;
}>({
  size: "default",
  variant: "default",
  spacing: 0
});
function ToggleGroup(t0) {
  const $ = _c(26);
  let children;
  let className;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      spacing: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    variant = $[6];
  }
  const spacing = t1 === undefined ? 0 : t1;
  let t2;
  if ($[7] !== spacing) {
    t2 = {
      "--gap": spacing
    };
    $[7] = spacing;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = t2 as React.CSSProperties;
  let t4;
  if ($[9] !== className) {
    t4 = cn("group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs", className);
    $[9] = className;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== size || $[12] !== spacing || $[13] !== variant) {
    t5 = {
      variant,
      size,
      spacing
    };
    $[11] = size;
    $[12] = spacing;
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== children || $[16] !== t5) {
    t6 = <ToggleGroupContext.Provider value={t5}>{children}</ToggleGroupContext.Provider>;
    $[15] = children;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props || $[19] !== size || $[20] !== spacing || $[21] !== t3 || $[22] !== t4 || $[23] !== t6 || $[24] !== variant) {
    t7 = <ToggleGroupPrimitive.Root data-slot="toggle-group" data-variant={variant} data-size={size} data-spacing={spacing} style={t3} className={t4} {...props}>{t6}</ToggleGroupPrimitive.Root>;
    $[18] = props;
    $[19] = size;
    $[20] = spacing;
    $[21] = t3;
    $[22] = t4;
    $[23] = t6;
    $[24] = variant;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function ToggleGroupItem(t0) {
  const $ = _c(19);
  let children;
  let className;
  let props;
  let size;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      children,
      variant,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    variant = $[5];
  }
  const context = React.useContext(ToggleGroupContext);
  const t1 = context.variant || variant;
  const t2 = context.size || size;
  const t3 = context.spacing;
  let t4;
  if ($[6] !== className || $[7] !== context.size || $[8] !== context.variant || $[9] !== size || $[10] !== variant) {
    t4 = cn(toggleVariants({
      variant: context.variant || variant,
      size: context.size || size
    }), "w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10", "data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-md data-[spacing=0]:last:rounded-r-md data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l", className);
    $[6] = className;
    $[7] = context.size;
    $[8] = context.variant;
    $[9] = size;
    $[10] = variant;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== children || $[13] !== context.spacing || $[14] !== props || $[15] !== t1 || $[16] !== t2 || $[17] !== t4) {
    t5 = <ToggleGroupPrimitive.Item data-slot="toggle-group-item" data-variant={t1} data-size={t2} data-spacing={t3} className={t4} {...props}>{children}</ToggleGroupPrimitive.Item>;
    $[12] = children;
    $[13] = context.spacing;
    $[14] = props;
    $[15] = t1;
    $[16] = t2;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
export { ToggleGroup, ToggleGroupItem };